import type { UseTranslationResponse } from 'react-i18next'

import { validateMediaTypeSchema } from '~/schemas/media'
import { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE } from '~/utils/constants'
import { FileUploadCode } from '~/utils/file-upload'

export const formatFileSize = (bytes: number): string => {
  if (bytes >= 1e9) {
    return `${Math.round(bytes / 1e9)}GB`
  }
  if (bytes >= 1e6) {
    return `${Math.round(bytes / 1e6)}MB`
  }
  if (bytes >= 1e3) {
    return `${Math.round(bytes / 1e3)}KB`
  }
  return `${bytes}B`
}

// Generate accept strings from validateMediaTypeSchema
const VALID_MIME_TYPES = validateMediaTypeSchema.options

export const ACCEPT_TYPE_MAPPINGS = {
  image: VALID_MIME_TYPES.filter((type) => type.startsWith('image/')).join(','),
  video: VALID_MIME_TYPES.filter((type) => type.startsWith('video/')).join(','),
  all: VALID_MIME_TYPES.join(','),
} as const

export const getAcceptString = (
  accept?: keyof typeof ACCEPT_TYPE_MAPPINGS
): string => {
  if (!accept) return ACCEPT_TYPE_MAPPINGS.all
  return ACCEPT_TYPE_MAPPINGS[accept]
}

export type FileValidationResult =
  | { success: true }
  | {
      success: false
      error: string
      errorCode: FileUploadCode.VALIDATION_ERROR | FileUploadCode.INVALID_FORMAT
    }

export const validateUploadFileType = (
  file: File,
  t: UseTranslationResponse<string>['t'],
  allowedKinds?: ('image' | 'video')[]
): FileValidationResult => {
  if (!file) {
    return {
      success: false,
      error: t('errors.file_type_invalid'),
      errorCode: FileUploadCode.INVALID_FORMAT,
    }
  }

  const { name, type, size } = file

  // Check file type first
  const shouldProceed = validateMediaTypeSchema.safeParse(type)
  if (!shouldProceed?.success) {
    return {
      success: false,
      error: t('errors.file_type_invalid', { fileName: name }),
      errorCode: FileUploadCode.INVALID_FORMAT,
    }
  }

  // Check if file kind is allowed
  if (allowedKinds && allowedKinds.length > 0) {
    const fileKind = type.startsWith('image/')
      ? 'image'
      : type.startsWith('video/')
        ? 'video'
        : null
    if (!fileKind || !allowedKinds.includes(fileKind)) {
      const allowedTypesText = allowedKinds.join(', ')
      return {
        success: false,
        error: t('errors.file_type_not_allowed', {
          fileName: name,
          allowedTypes: allowedTypesText,
        }),
        errorCode: FileUploadCode.INVALID_FORMAT,
      }
    }
  }

  // Check file size based on type
  if (type === 'video/mp4' && size >= MAX_VIDEO_FILE_SIZE) {
    const errorMessage = t('errors.file_size_exceeded', {
      mediaType: 'Video',
      maxSize: formatFileSize(MAX_VIDEO_FILE_SIZE),
    })

    return {
      success: false,
      error: errorMessage,
      errorCode: FileUploadCode.VALIDATION_ERROR,
    }
  }

  if (type !== 'video/mp4' && size >= MAX_IMAGE_FILE_SIZE) {
    const errorMessage = t('errors.file_size_exceeded', {
      mediaType: 'Image',
      maxSize: formatFileSize(MAX_IMAGE_FILE_SIZE),
    })

    return {
      success: false,
      error: errorMessage,
      errorCode: FileUploadCode.VALIDATION_ERROR,
    }
  }

  return { success: true }
}
